import { AionCta } from "@/components/site/AionCta";
import { KeywordHubLinks } from "@/components/site/KeywordHubLinks";
import { PageBreadcrumb } from "@/components/site/PageBreadcrumb";
import { hubsByTag } from "@/lib/keyword-hubs";
import { createFileRoute, Link } from "@tanstack/react-router";

export const Route = createFileRoute("/about")({
  head: () => ({
    meta: [
      { title: "Who Is Raj Shamani? Official Biography & Career Story", key: "title" },
      { name: "description", content: "The official Raj Shamani biography — from an Indore factory floor to the United Nations, Figuring Out, House of X and 20+ startup investments.", key: "description" },
      { name: "geo.region", content: "IN", key: "geo.region" },
      { name: "geo.placename", content: "Mumbai, India", key: "geo.placename" },
      { property: "og:title", content: "Who Is Raj Shamani? Official Biography & Career Story", key: "og:title" },
      { property: "og:description", content: "The official Raj Shamani biography — from an Indore factory floor to the United Nations, Figuring Out, House of X and 20+ startup investments.", key: "og:description" },
      { property: "og:url", content: "https://rajshamani.life/about", key: "og:url" },
      { property: "og:type", content: "profile", key: "og:type" },
      { property: "og:site_name", content: "Raj Shamani", key: "og:site_name" },
      { property: "profile:first_name", content: "Raj", key: "profile:first_name" },
      { property: "profile:last_name", content: "Shamani", key: "profile:last_name" },
      { property: "profile:username", content: "rajshamani", key: "profile:username" },
      { name: "twitter:card", content: "summary_large_image", key: "twitter:card" },
      { name: "twitter:title", content: "Who Is Raj Shamani? Official Biography & Career Story", key: "twitter:title" },
      { name: "twitter:description", content: "The official Raj Shamani biography — from an Indore factory floor to the United Nations, Figuring Out, House of X and 20+ startup investments.", key: "twitter:description" },
    ],

    links: [{ rel: "canonical", href: "https://rajshamani.life/about" }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Person",
          name: "Raj Shamani",
          givenName: "Raj",
          familyName: "Shamani",
          jobTitle: "Entrepreneur, Angel Investor, Podcaster, Global Speaker",
          birthDate: "1996-03-22",
          birthPlace: {
            "@type": "Place",
            name: "Indore, Madhya Pradesh, India",
            address: {
              "@type": "PostalAddress",
              addressLocality: "Indore",
              addressRegion: "Madhya Pradesh",
              addressCountry: "IN",
            },
          },
          nationality: "Indian",
          gender: "Male",
          image: "https://rajshamani.life/raj-shamani.jpg", // TODO: replace with actual headshot URL
          url: "https://rajshamani.life/",
          description:
            "Indian entrepreneur, angel investor, content creator, global speaker, and host of the Figuring Out podcast. Director at Shamani Industries, a leading Indian manufacturer of polymers and additives. Backed 20+ Indian startups across D2C, fintech, and creator economy.",
          sameAs: [
            "https://instagram.com/rajshamani",
            "https://linkedin.com/in/rajshamani",
            "https://youtube.com/@RajShamani",
            "https://twitter.com/rajshamani",
            "https://en.wikipedia.org/wiki/Raj_Shamani",
          ],
          worksFor: [
            {
              "@type": "Organization",
              name: "Shamani Industries",
              description: "Leading Indian manufacturer of polymers and additives",
              url: "https://shamaniindustries.com",
            },
            {
              "@type": "Organization",
              name: "Figuring Out Media",
              description: "Business podcast and media company",
            },
          ],
          knowsAbout: [
            "Entrepreneurship",
            "Angel Investing",
            "Direct-to-Consumer Brands",
            "Content Creation",
            "Public Speaking",
            "Indian Startup Ecosystem",
            "Business Strategy",
            "Personal Branding",
          ],
          alumniOf: {
            "@type": "EducationalOrganization",
            name: "Daly College",
            address: {
              "@type": "PostalAddress",
              addressLocality: "Indore",
              addressRegion: "Madhya Pradesh",
              addressCountry: "IN",
            },
          },
          award: "United Nations Speaker — Young speaker at the United Nations on entrepreneurship",
        }),
      },
      // FAQPage schema intentionally lives only on /faq (canonical page)
      // to avoid FAQ rich-result cannibalization across duplicate pages.

    ],
  }),
  component: About,
});

const milestones = [
  { y: "1996", t: "Born in Indore, Madhya Pradesh", d: "Raj Shamani was born on 22 March 1996 in Indore, Madhya Pradesh, into a Marwari business family. His father ran Shamani Industries, a leading Indian manufacturer of polymers and additives for the automotive and construction industries." },
  { y: "2004", t: "Early exposure to business", d: "Growing up in a family manufacturing business, Raj began visiting factories and understanding supply chains from a young age. The Marwari business ethos of frugality, long-term thinking, and relationship-building shaped his early worldview." },
  { y: "2012", t: "Joined Shamani Industries", d: "At age 16, Raj formally joined the family business, Shamani Industries. He started on the factory floor, learning procurement, manufacturing processes, and B2B distribution. This hands-on experience gave him a deep understanding of Indian manufacturing at scale." },
  { y: "2014", t: "Completed schooling at Daly College", d: "Raj graduated from Daly College, Indore — one of India's oldest and most prestigious boarding schools. Founded in 1870, Daly College has educated generations of Indian business leaders and royalty." },
  { y: "2015", t: "First public speaking experience", d: "Raj began speaking at local entrepreneurship events and college festivals in Indore. He shared raw, practical lessons from running a manufacturing business — a refreshing contrast to theoretical startup advice." },
  { y: "2017", t: "United Nations Speaker", d: "Raj Shamani was invited to speak at the United Nations on youth entrepreneurship. This marked his arrival on the global stage. He would go on to speak at over 150 stages across 25+ countries, including TEDx and major business conferences." },
  { y: "2018", t: "Began building a personal brand on social media", d: "Raj started posting business lessons on LinkedIn and Instagram. Unlike polished corporate content, his posts shared real numbers, failed experiments, and honest observations from running a factory. This authenticity built a rapidly growing audience." },
  { y: "2019", t: "Crossed 500K followers across platforms", d: "By consistently posting daily business content, Raj grew his combined following to over 500,000. His content mix included factory walkthroughs, B2B sales tips, negotiation lessons, and honest reflections on family business dynamics." },
  { y: "2020", t: "Scaled personal brand to multi-million reach", d: "Raj's Instagram, LinkedIn, and YouTube following crossed into the multi-millions. He became one of India's most-followed business voices, known for blending manufacturing expertise with startup culture insights." },
  { y: "2021", t: "Launched Figuring Out Podcast", d: "Raj launched Figuring Out, a long-form interview podcast where he sits down with India's top founders, investors, and creators. The podcast quickly became one of India's most-watched business shows, available on YouTube, Spotify, and Apple Podcasts." },
  { y: "2022", t: "Started angel investing", d: "Raj began writing angel cheques into early-stage Indian startups. His investment thesis focused on D2C brands, fintech, creator economy tools, and businesses with strong unit economics. He has since backed 20+ startups." },
  { y: "2023", t: "Co-founded House of X", d: "Raj co-founded House of X, a venture studio and consumer brand house that builds and scales D2C brands in India. House of X applies his manufacturing and distribution expertise to create high-quality, mass-market consumer products." },
  { y: "2024", t: "Expanded Figuring Out into a media company", d: "Figuring Out evolved from a podcast into a full media company with a team, production studio, and multiple content formats. The show regularly hosts India's unicorn founders, Bollywood celebrities, and global business leaders." },
  { y: "Today", t: "Founder, Investor & Podcaster", d: "Raj Shamani continues to direct Shamani Industries, co-run House of X, host Figuring Out, mentor founders, and invest in the next generation of Indian startups. He splits his time between Indore, Mumbai, and international speaking engagements." },
];

const keyStats = [
  { label: "Countries spoken in", value: "25+" },
  { label: "Stages addressed", value: "150+" },
  { label: "Startups invested in", value: "20+" },
  { label: "Social followers", value: "5M+" },
  { label: "Podcast episodes", value: "200+" },
  { label: "Years in business", value: "12+" },
];

/**
 * Only questions this page is the best answer for. Age, net worth, education,
 * family, Shamani Industries and the book each have a dedicated page — keeping
 * full duplicate answers here made /about outrank those pages with a weaker
 * snippet, so those questions link out instead.
 */
const faqs: { q: string; a: string }[] = [
  {
    q: "Who is Raj Shamani?",
    a: "Raj Shamani is an Indian entrepreneur, angel investor, content creator, and global speaker. He is the director of Shamani Industries, host of the Figuring Out podcast (one of India's most-watched business podcasts), and an active angel investor in 20+ Indian startups. He has spoken at the United Nations and over 150 stages across 25+ countries.",
  },
  {
    q: "What companies and ventures does Raj Shamani run?",
    a: "Raj Shamani is a director at Shamani Industries (a leading Indian manufacturer of polymers and additives), co-founder of House of X (a D2C venture studio), and founder of Figuring Out Media. He is also an active angel investor with a portfolio of 20+ Indian startups across D2C, fintech, consumer, and creator-economy categories. See the Ventures page for the latest portfolio.",
  },
  {
    q: "What is the Figuring Out podcast?",
    a: "Figuring Out is Raj Shamani's long-form interview podcast where he sits down with India's top founders, investors, creators, and cultural voices for honest conversations about business, money, and building. It is available on YouTube, Spotify, and Apple Podcasts and is one of the most-watched business podcasts in India.",
  },
  {
    q: "Where is Raj Shamani from?",
    a: "Raj Shamani was born and raised in Indore, Madhya Pradesh, in a Marwari business family. His family runs Shamani Industries, a manufacturing company based in Indore. Raj is based in India and travels internationally for speaking engagements, podcast collaborations, and investor meetings.",
  },
  {
    q: "Has Raj Shamani spoken at the United Nations?",
    a: "Yes, Raj Shamani spoke at the United Nations on youth entrepreneurship in 2017. This was a major milestone in his speaking career, and he has since addressed audiences at over 150 stages across more than 25 countries.",
  },
  {
    q: "How can I contact Raj Shamani or be a guest on Figuring Out?",
    a: "For brand collaborations, speaking enquiries, and partnerships, use the Contact page on this website. To pitch yourself as a guest on the Figuring Out podcast, fill out the form on the Be a Guest page — every application is reviewed by his team.",
  },
  {
    q: "Where can I watch Raj Shamani on YouTube?",
    a: "Raj Shamani publishes every Figuring Out podcast episode on his YouTube channel @RajShamani, along with shorter business clips. Full episodes are also available on Spotify and Apple Podcasts, and each episode on this site includes the YouTube video plus a written summary.",
  },
];

/** Questions answered in full on their own page — linked, not duplicated. */
const answeredElsewhere: { q: string; to: string; a: string }[] = [
  {
    q: "How old is Raj Shamani?",
    to: "/raj-shamani-age",
    a: "Born 22 March 1996 — full date of birth, current age and birthday.",
  },
  {
    q: "What is Raj Shamani's net worth?",
    to: "/raj-shamani-net-worth",
    a: "Never officially disclosed — his five real income streams explained.",
  },
  {
    q: "What is Raj Shamani's education and qualification?",
    to: "/raj-shamani-education",
    a: "Daly College, Indore — his degree, and why he skipped an MBA.",
  },
  {
    q: "Who is Raj Shamani's father and family?",
    to: "/raj-shamani-family",
    a: "His Marwari family in Indore and his father's manufacturing business.",
  },
  {
    q: "What is Shamani Industries?",
    to: "/shamani-industries",
    a: "The family polymers and additives business, and Raj's role in it.",
  },
  {
    q: "Which books has Raj Shamani written?",
    to: "/raj-shamani-books",
    a: "Build, Don't Talk — summary, core ideas, price in India and where to buy.",
  },
];


const quickFacts: { k: string; v: string }[] = [
  { k: "Full name", v: "Raj Shamani" },
  { k: "Date of birth", v: "22 March 1996" },
  { k: "Age", v: "30 years (as of 2026)" },
  { k: "Birthplace", v: "Indore, Madhya Pradesh, India" },
  { k: "Nationality", v: "Indian" },
  { k: "Education", v: "Daly College, Indore" },
  { k: "Family", v: "Marwari business family; father runs Shamani Industries" },
  { k: "Profession", v: "Entrepreneur, angel investor, podcaster, speaker" },
  { k: "Company", v: "Shamani Industries (director), House of X (co-founder)" },
  { k: "Podcast", v: "Figuring Out with Raj Shamani" },
  { k: "Book", v: "Build, Don't Talk (2022)" },
  { k: "Net worth", v: "Not officially disclosed; public estimates are speculative" },
];

function About() {
  return (
    <div>
      <PageBreadcrumb current="About" />
      <section className="bg-noir border-b border-border/50">
        <div className="mx-auto max-w-5xl px-6 py-24">
          <div className="text-xs tracking-widest uppercase text-gold mb-4">Official Biography</div>
          <h1 className="text-4xl md:text-5xl font-bold leading-tight">
            Who Is Raj Shamani? The Official Biography
          </h1>

          <p className="mt-2 text-xl text-muted-foreground">
            Entrepreneur, Angel Investor, Podcaster & Global Speaker
          </p>
          <p className="mt-8 text-lg text-muted-foreground max-w-3xl leading-relaxed">
            Raj Shamani (born 22 March 1996, Indore, Madhya Pradesh) is an Indian
            entrepreneur, angel investor, content creator, and global speaker. He is the
            director of <span className="text-gold">Shamani Industries</span>, a leading
            manufacturer of polymers and additives, and the host of{" "}
            <span className="text-gold">Figuring Out</span>, one of India's most-watched
            long-form business podcasts.
          </p>
          <p className="mt-4 text-lg text-muted-foreground max-w-3xl leading-relaxed">
            Raj has spoken at the <span className="text-gold">United Nations</span> and
            over 150 stages across 25+ countries. He has backed 20+ Indian startups as an
            angel investor, co-founded <span className="text-gold">House of X</span>, a D2C
            venture studio, and built one of India's largest personal brands in the business
            space — all before turning 30.
          </p>
        </div>
      </section>

      <section className="bg-card/30 border-b border-border/50">
        <div className="mx-auto max-w-5xl px-6 py-16">
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-8">
            {keyStats.map((s, i) => (
              <div key={i} className="text-center">
                <div className="text-3xl md:text-4xl font-bold text-gold font-display">{s.value}</div>
                <div className="text-sm text-muted-foreground mt-2">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-5xl px-6 pt-24">
        <div className="text-xs tracking-widest uppercase text-gold mb-4">Quick Facts</div>
        <h2 className="text-3xl md:text-4xl font-bold mb-8">
          Raj Shamani wiki: age, family, education & net worth at a glance
        </h2>
        <dl className="grid sm:grid-cols-2 gap-x-10">
          {quickFacts.map((f, i) => (
            <div key={i} className="flex gap-4 py-4 border-b border-border/50">
              <dt className="w-40 shrink-0 text-sm uppercase tracking-wide text-gold">{f.k}</dt>
              <dd className="text-muted-foreground">{f.v}</dd>
            </div>
          ))}
        </dl>
        <p className="mt-8 text-muted-foreground">
          Deep dives:{" "}
          {hubsByTag("bio").map((h, i, arr) => (
            <span key={h.to}>
              <Link to={h.to} className="text-gold underline underline-offset-4">
                {h.inlineLabel}
              </Link>
              {i < arr.length - 2 ? ", " : i === arr.length - 2 ? ", and " : "."}
            </span>
          ))}
        </p>
      </section>




      <section className="mx-auto max-w-5xl px-6 py-24">
        <div className="text-xs tracking-widest uppercase text-gold mb-4">Timeline</div>
        <h2 className="text-3xl md:text-4xl font-bold mb-4">The Journey</h2>
        <p className="text-muted-foreground max-w-2xl mb-16">
          From a factory floor in Indore to the United Nations, podcast studios, and
          venture-capital term sheets. Every milestone shaped the entrepreneur, investor,
          and creator Raj Shamani is today.
        </p>

        <div className="relative">
          <div className="absolute left-[19px] md:left-[59px] top-0 bottom-0 w-px bg-border/50" />
          <div className="space-y-0">
            {milestones.map((m, i) => (
              <div key={i} className="relative grid md:grid-cols-[120px_1fr] gap-6 pb-12 last:pb-0">
                <div className="flex items-start gap-4 md:block">
                  <div className="relative z-10 w-10 h-10 rounded-full bg-noir border-2 border-gold flex items-center justify-center shrink-0">
                    <div className="w-2 h-2 rounded-full bg-gold" />
                  </div>
                  <div className="text-gold font-display font-bold text-2xl md:mt-0">{m.y}</div>
                </div>
                <div className="md:pt-1">
                  <h3 className="text-xl font-bold mb-2">{m.t}</h3>
                  <p className="text-muted-foreground leading-relaxed">{m.d}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-5xl px-6 pb-24">
        <div className="grid md:grid-cols-2 gap-6">
          <div className="p-8 rounded-xl bg-card border border-border">
            <h3 className="text-2xl font-bold mb-3 text-gradient-gold">Philosophy</h3>
            <p className="text-muted-foreground leading-relaxed">
              "You can't out-strategize hard work. Show up daily, share what
              you're learning, and the right people will find you. The best
              investment you can make is in your own clarity — know what you
              want, know what you're good at, and be honest about both."
            </p>
          </div>
          <div className="p-8 rounded-xl bg-card border border-border">
            <h3 className="text-2xl font-bold mb-3 text-gradient-gold">Mission</h3>
            <p className="text-muted-foreground leading-relaxed">
              Help a million Indians think bigger about business, money, and
              what's possible — through honest stories, early-stage capital, and
              conversations that don't shy away from the messy reality of building.
            </p>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-5xl px-6 pb-24">
        <div className="text-xs tracking-widest uppercase text-gold mb-4">FAQ</div>
        <h2 className="text-3xl md:text-4xl font-bold mb-12">
          Frequently asked about Raj Shamani
        </h2>
        <div className="space-y-8">
          {faqs.map((f, i) => (
            <div key={i} className="pb-8 border-b border-border/50 last:border-0">
              <h3 className="text-xl font-bold mb-3">{f.q}</h3>
              <p className="text-muted-foreground leading-relaxed">{f.a}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="mx-auto max-w-5xl px-6 pb-24">
        <div className="text-xs tracking-widest uppercase text-gold mb-4">Answered in detail</div>
        <h2 className="text-3xl md:text-4xl font-bold mb-3">
          Age, net worth, education & family
        </h2>
        <p className="text-muted-foreground mb-8 max-w-2xl">
          Each of these has its own page with the full answer, sources and context.
        </p>
        <ul className="grid sm:grid-cols-2 gap-3">
          {answeredElsewhere.map((a) => (
            <li key={a.to}>
              <Link
                to={a.to}
                className="block rounded-lg border border-border bg-card px-4 py-3 hover:border-gold/50 transition"
              >
                <span className="font-semibold block">{a.q}</span>
                <span className="text-sm text-muted-foreground leading-relaxed">{a.a}</span>
              </Link>
            </li>
          ))}
        </ul>
      </section>


      <AionCta />
      <KeywordHubLinks className="pb-24" />

    </div>
  );
}
